import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { Button } from "./Button";

/**
 * A small, RTL, theme-styled confirmation modal — reused app-wide to guard a
 * destructive/irreversible action behind an explicit «yes / no». Defaults are the
 * shared exit-from-game copy (`fa.confirm.*`); every field is overridable so the
 * same dialog serves any confirm-then-act flow.
 *
 * It is a controlled overlay (mount it when you want it shown, like `WinOverlay`):
 * `onCancel` fires on the backdrop tap, the «ماندن» button, or the Esc-equivalent;
 * `onConfirm` fires only from the primary confirm button. Buttons reuse the shared
 * `Button` (tap sound + `.btn` design tokens); the confirm action is `danger`-styled
 * so the irreversible choice reads clearly. Matches `WinOverlay`'s dim + card look.
 *
 * TWO-action variant: pass `secondaryLabel` + `onSecondary` to add a SECOND distinct
 * confirm action between the primary confirm and cancel (e.g. the offline-game exit's
 * «خروج» keep-resumable vs «پایان بازی» end-for-good). It is `danger`-styled by default
 * (`secondaryDanger`); the primary confirm is then typically the neutral choice
 * (`danger={false}` → primary/gold). Omit it and the dialog is the classic single-confirm.
 */
export function ConfirmDialog({
  title = fa.confirm.exitTitle,
  body = fa.confirm.exitBody,
  confirmLabel = fa.confirm.exitConfirm,
  cancelLabel = fa.confirm.exitCancel,
  onConfirm,
  onCancel,
  danger = true,
  secondaryLabel,
  onSecondary,
  secondaryDanger = true,
}: {
  title?: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Style the confirm button as destructive (default) vs. primary. */
  danger?: boolean;
  /** Optional SECOND confirm action's label — renders a third button when set with
   *  {@link onSecondary}. */
  secondaryLabel?: string;
  /** Handler for the optional second confirm action. */
  onSecondary?: () => void;
  /** Style the second action as destructive (default) vs. primary. */
  secondaryDanger?: boolean;
}): ReactNode {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      dir="rtl"
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.6)",
        // Backdrop fades in while the card pops — the same paired entrance the
        // shared Sheet uses (bg-fadein + a panel motion), so no abrupt dim.
        animation: "bg-fadein 0.2s ease-out",
      }}
    >
      <div
        // Stop backdrop-dismiss when tapping inside the card.
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: "var(--radius-lg)",
          padding: "26px 22px",
          width: "100%",
          maxWidth: 320,
          textAlign: "center",
          boxShadow: "0 30px 70px rgba(0,0,0,.5)",
          animation: "bg-pop .3s ease",
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 6 }} aria-hidden>
          🚪
        </div>
        <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 800 }}>{title}</h2>
        <p style={{ margin: "0 0 20px", fontSize: 14, color: "var(--hint)" }}>{body}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm}>
            {confirmLabel}
          </Button>
          {secondaryLabel && onSecondary ? (
            <Button variant={secondaryDanger ? "danger" : "primary"} onClick={onSecondary}>
              {secondaryLabel}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onCancel}>
            {cancelLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
